import { Label, NativeSelect, NativeSelectOption, ScrollArea, Separator } from 'shared-ui';

import type { StatsPeriod } from '@/api/statsApi';
import { PanelCloseButton, SlidingPanel } from '@/components/shared/SlidingPanel';
import { ToggleRow } from '@/components/shared/ToggleRow';
import { STAT_DEFINITIONS, type StatId, type StatsPreferenceScope } from '@/hooks/useStatsPreferences';

export interface StatsConfigPanelProps {
    isOpen: boolean;
    visibleStats: StatId[];
    onToggle: (id: StatId, enabled: boolean) => void;
    onClose: () => void;
    period: StatsPeriod;
    onPeriodChange: (period: StatsPeriod) => void;
    /**
     * Which header is being configured. The explorer offers every statistic, including the
     * estate-only ones; a client's Find & Fix (the default) never sees those.
     */
    scope?: StatsPreferenceScope;
}

const CATEGORY_LABELS: Record<string, string> = {
    mostFrequent: 'Most Frequent Stats',
    originNoDig: 'Origin No Dig® Stats',
    pipeline: 'Pipeline Stats',
};

const CATEGORY_ORDER = ['mostFrequent', 'originNoDig', 'pipeline'] as const;

function StatsConfigPanelContent({
    visibleStats,
    onToggle,
    onClose,
    period,
    onPeriodChange,
    scope = 'client',
}: Omit<StatsConfigPanelProps, 'isOpen'>) {
    const statsByCategory = CATEGORY_ORDER.map((category) => ({
        category,
        label: CATEGORY_LABELS[category],
        // Explorer-only stats have no single-client meaning, so only the explorer offers them.
        stats: STAT_DEFINITIONS.filter(
            (def) => def.category === category && (scope === 'explorer' || !def.explorerOnly),
        ),
    }));

    return (
        <>
            <div className="flex h-20 shrink-0 items-center justify-between px-6">
                <h2 className="text-lg leading-7 font-black tracking-normal uppercase md:text-2xl md:leading-8">
                    Stats
                </h2>
                <PanelCloseButton onClick={onClose} aria-label="Close stats configuration panel" />
            </div>

            <Separator />

            <ScrollArea className="min-h-0 flex-1">
                <div className="space-y-6 px-6 py-4">
                    {/* Show period — matches FiltersPanel dropdown style */}
                    <div className="space-y-1.5">
                        <Label htmlFor="stats-show-period" className="text-foreground text-sm leading-5 font-medium">
                            Show
                        </Label>
                        <NativeSelect
                            id="stats-show-period"
                            value={period}
                            onChange={(e) => {
                                onPeriodChange(e.target.value as StatsPeriod);
                            }}
                            className="panel-select text-muted-foreground w-full text-sm leading-5 font-normal tracking-normal"
                        >
                            <NativeSelectOption value="overall">Overall</NativeSelectOption>
                            <NativeSelectOption value="thirty">Last 30 Days</NativeSelectOption>
                        </NativeSelect>
                    </div>

                    {/* Visible stats toggles grouped by category */}
                    {statsByCategory.map(({ category, label, stats }) => (
                        <div key={category}>
                            <p className="text-foreground mb-2 text-sm leading-5 font-medium">{label}</p>
                            {stats.map((def) => {
                                const checked = visibleStats.includes(def.id);
                                const isLast = checked && visibleStats.length === 1;
                                return (
                                    <ToggleRow
                                        key={def.id}
                                        label={def.label}
                                        checked={checked}
                                        disabled={isLast}
                                        onCheckedChange={(v) => {
                                            onToggle(def.id, v);
                                        }}
                                    />
                                );
                            })}
                        </div>
                    ))}
                </div>
            </ScrollArea>
        </>
    );
}

export function StatsConfigPanel(props: StatsConfigPanelProps) {
    return (
        <SlidingPanel
            isOpen={props.isOpen}
            onClose={props.onClose}
            ariaLabel="Stats configuration"
            title="Stats Configuration"
        >
            <StatsConfigPanelContent {...props} />
        </SlidingPanel>
    );
}
